import { FC, useEffect, useMemo, useState } from "react";
import { HeaderComponent } from "../../components";
import HeartRateImage from "../../assets/images/onboarding/heartrate.png";
import {
    IonButton,
    IonCard,
    IonContent,
    IonFooter,
    IonItem,
    IonLabel,
    IonLoading,
    IonPage,
    IonProgressBar,
    IonText,
    useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useMatch } from "../../components/hooks/match";
import { HrData, MatchPhone } from "refsix-js-models";
import {
    FitnessDataProvider,
    useFitnessDataContext,
} from "../../components/hooks";
import { trackEvent } from "../../services/analytics/analyticsService";
import { routes } from "../../route/constants";
import "./onBoardingShareHealthData.css";
import "../register/register.css";
import { isIos } from "../../services/platformDetection";
import HeartRateGraphComponent from "../../components/HeartRateGraphComponent";
import { ScorzaCard } from "../../components/stats/ScorzaCard";
import exampleMatch from "../../support/example-result.json";
import { setShowOnBoardingFlow } from "../../redux/actions/appStatus";

const demoMatchId = "example-result1";
const fallbackMatch = exampleMatch.match as unknown as MatchPhone;
const fallbackHrData = exampleMatch.hr as HrData;

export const OnBoardingShareHealthDataWrapper: React.FC = () => {
    return (
        <FitnessDataProvider matchId={demoMatchId}>
            <OnBoardingShareHealthData />
        </FitnessDataProvider>
    );
};

export const OnBoardingShareHealthData: FC = () => {
    const { t } = useTranslation();
    const route = useIonRouter();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const demoMatch = useMatch(demoMatchId);
    const matchForDisplay = useMemo<MatchPhone>(
        () => demoMatch ?? fallbackMatch,
        [demoMatch]
    );
    const [filteredHrData, setFilteredHrData] =
        useState<HrData>(fallbackHrData);
    const { hrData, loading } = useFitnessDataContext();

    useEffect(() => {
        if (
            demoMatch?.matchFinished &&
            hrData?.heartRateValues &&
            hrData.heartRateValues.length > 0
        ) {
            setFilteredHrData(hrData);
        }
    }, [hrData, demoMatch]);

    const hasHeartRateData =
        filteredHrData?.heartRateValues &&
        filteredHrData.heartRateValues.length > 0;

    // var _areHealthPermissionsDetermined = function () {
    //     var keys = Object.keys(self.permissions.health);
    //     var result = false;
    //     keys.forEach(function (key) {
    //         if (
    //             self.permissions.health[key] !==
    //             PermissionsService.NOT_DETERMINED
    //         ) {
    //             result = true;
    //         }
    //     });
    //     return result;
    // };
    //
    // self.requestHealthPermissions = function () {
    //     if (_areHealthPermissionsDetermined()) {
    //         window.open("x-apple-health://", "_system"); // use inappbrowser plugin to open in safari
    //     } else {
    //         PermissionsService.requestIosWorkoutPermission().then(function (
    //             res
    //         ) {
    //             self.updatePermissions();
    //         });
    //     }
    // };

    const directToNextPage = () => {
        dispatch(setShowOnBoardingFlow(false));
        route.push(routes.tabResults, "root", "replace");
    };

    const askForPermission = async () => {
        if (isIos() && window.iospermissions) {
            setIsLoading(true);
            return new Promise((resolve, reject) => {
                window.iospermissions?.requestWorkoutPermissions(
                    (permissions: any) => {
                        setIsLoading(false);
                        trackEvent("shareHealthData", {});
                        directToNextPage();
                        resolve(permissions);
                    },
                    (error: any) => {
                        setIsLoading(false);
                        directToNextPage();
                        reject(error);
                    }
                );
            });
        }
        setIsLoading(false);
        directToNextPage();
    };

    return (
        <IonPage>
            <HeaderComponent
                showBackButton={false}
                title={t("help.onBoarding.performanceData")}
                showProgressBar={true}
                progressValue={0.9}
            />
            <IonContent>
                <IonLoading isOpen={isLoading} />
                <h2 className="onboarding-section-header">
                    {t("help.onBoarding.shareHealthData")}
                </h2>{" "}
                <IonItem lines="none" color="transparent">
                    <IonText className="align-center">
                        {t("help.onBoarding.trackFitness")}
                    </IonText>
                </IonItem>
                {hasHeartRateData && (
                    <ScorzaCard title={"Heart Rate Zones"}>
                        <HeartRateGraphComponent
                            match={matchForDisplay}
                            filteredHrData={filteredHrData}
                        />
                    </ScorzaCard>
                )}
                {!hasHeartRateData && !loading && (
                    <IonCard
                        color="transparent"
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <IonItem lines="none" color="transparent">
                            <img src={HeartRateImage} alt="HearRate Image" />
                        </IonItem>
                    </IonCard>
                )}
            </IonContent>
            <IonFooter>
                <IonButton
                    expand="block"
                    data-testid={"share-health-data-next-button"}
                    onClick={() => askForPermission()}
                >
                    {t("general.next")}
                </IonButton>
            </IonFooter>
        </IonPage>
    );
};
